import useSWR from "swr";
import queryString from "query-string";
import apiClient from "../utils/apiClient";

/**
 * Hook to obtain research filtered by client and with pagination
 * @param {string|number} clientId - Client ID
 * @param {object} filters - Filters such as status, name, email, etc.
 * @param {number} page - Page number
 */
export const useGetResearches = (filters = {}, page = 1) => {
  const cleanFilters = { ...filters };
  if (cleanFilters.status === "all") {
    delete cleanFilters.status;
  }

  const queryParams = {
    page,
    ...cleanFilters,
  };

  const query = queryString.stringify(queryParams, {
    skipNull: true,
    skipEmptyString: true,
  });

  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error } = useSWR(`/researches/?${query}`, fetcher);

  return {
    data,
    isLoading: !error && !data,
    error,
  };
};

/**
 * Hook to fetch payable researches
 * @returns {object} Object containing:
 * @returns {array} data - The list of payable researches when loaded
 * @returns {boolean} isLoading - True while the data is being fetched
 * @returns {object} error - Error object if the request fails
 */
export const usePayableResearches = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error } = useSWR(`/researches/payable`, fetcher);

  return {
    data,
    isLoading: !error && !data,
    error,
  };
};

export const requestPayout = (data) => {
  return apiClient.post(`/researches/payouts/`, data).then((res) => res.data);
};

export const usePayout = (id) => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error, mutate } = useSWR(`/researches/payouts/${id}`, fetcher);

  return {
    data,
    isLoading: !error && !data,
    error,
    mutate,
  };
};

export const getPresignedUrl = (payoutId, fileType) => {
  return apiClient
    .post(`/researches/payouts/${payoutId}/presigned-url/`, {
      file_type: fileType,
    })
    .then((res) => res.data);
};

export const uploadFileToS3 = (presignedUrl, file) => {
  return fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  });
};

export const updatePayoutInvoice = (payoutId, invoiceData) => {
  return apiClient
    .patch(`/researches/payouts/${payoutId}/update-invoice/`, invoiceData)
    .then((res) => res.data);
};

export const usePayouts = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error, mutate } = useSWR(`/researches/payouts`, fetcher);

  return {
    data,
    isLoading: !error && !data,
    error,
    mutate,
  };
};
